import React, { useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import UtilityCard from '../components/UtilityCard';
import './Home.css';
import './HomePremium.css';

const utilities = [
  {
    id: 'json-formatter',
    title: 'JSON Formatter',
    description: 'Format and validate JSON data',
    icon: '📄',
    category: 'Data & Dev Utilities',
    path: '/utility/json-formatter'
  },
  {
    id: 'hash-generator',
    title: 'Hash Generator',
    description: 'Generate MD5, SHA1, SHA256, SHA512 hashes',
    icon: '🔐',
    category: 'Data & Dev Utilities',
    path: '/utility/hash-generator'
  },
  {
    id: 'base64-encoder',
    title: 'Base64 Encoder/Decoder',
    description: 'Encode and decode Base64 strings',
    icon: '🔑',
    category: 'Data & Dev Utilities',
    path: '/utility/base64-encoder'
  },
  {
    id: 'uuid-generator',
    title: 'UUID Generator',
    description: 'Generate unique UUIDs',
    icon: '🆔',
    category: 'Data & Dev Utilities',
    path: '/utility/uuid-generator'
  },
  {
    id: 'timestamp-converter',
    title: 'Timestamp Converter',
    description: 'Convert between timestamps and dates',
    icon: '⏰',
    category: 'Data & Dev Utilities',
    path: '/utility/timestamp-converter'
  },
  {
    id: 'qr-generator',
    title: 'QR Code Generator',
    description: 'Generate QR codes for any text',
    icon: '📱',
    category: 'Everyday Tools',
    path: '/utility/qr-generator'
  },
  {
    id: 'password-generator',
    title: 'Password Generator',
    description: 'Generate secure random passwords',
    icon: '🔒',
    category: 'Everyday Tools',
    path: '/utility/password-generator'
  },
  {
    id: 'unit-converter',
    title: 'Unit Converter',
    description: 'Convert between different units',
    icon: '📏',
    category: 'Everyday Tools',
    path: '/utility/unit-converter'
  }
];

const Home = () => {
  const { isAuthenticated, loading } = useAuth();

  // Redirect authenticated users to dashboard
  if (!loading && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="home">
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">DevPartners - Custom Solutions Platform</h1>
          <p className="hero-subtitle">
            Explore powerful utilities, games, chat, blog, customised AI tools and more. Need something custom? We build it.
          </p>
          {!isAuthenticated && (
            <div className="hero-buttons">
              <Link to="/register" className="btn btn-primary">Get Started</Link>
              <Link to="/login" className="btn btn-secondary">Login</Link>
            </div>
          )}
        </div>
      </section>

      <section className="utilities-section">
        <div className="container">
          <h2 className="section-title">Available Utilities</h2>
          <p className="section-subtitle">Free tools for everyone</p>
          <div className="utilities-grid">
            {utilities.map(utility => (
              <UtilityCard key={utility.id} utility={utility} />
            ))}
          </div>
        </div>
      </section>

      <section className="games-section">
        <div className="container">
          <h2 className="section-title">🎮 Games</h2>
          <p className="section-subtitle">Play exciting games and compete with other users</p>
          <div className="games-grid">
            <Link to="/game/tic-tac-toe" className="game-card">
              <div className="game-icon">⭕</div>
              <h3>Tic Tac Toe</h3>
              <p>Play Tic Tac Toe with friends or computer</p>
              <span className="badge-free">Free to Play</span>
            </Link>
            <Link to="/game/mosquito" className="game-card">
              <div className="game-icon">🦟</div>
              <h3>Kill the Mosquito</h3>
              <p>Test your reflexes and accuracy</p>
              <span className="badge-free">Free to Play</span>
            </Link>
            <Link to="/game/dragon" className="game-card">
              <div className="game-icon">🐉</div>
              <h3>Drag the Dragon</h3>
              <p>Guide the dragon through obstacles</p>
              <span className="badge-free">Free to Play</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="premium-section">
        <div className="container">
          <h2 className="section-title">Premium Features</h2>
          <p className="section-subtitle">
            Register and login to access these features. All features include rate limiting to ensure fair usage. 
            Free tier users have generous limits, and premium members enjoy unlimited access.
          </p>
          <div className="premium-grid">
            <div className="premium-card">
              <div className="premium-icon">💬</div>
              <h3>Online Chat</h3>
              <p>Chat with other users, create chatrooms, and connect with the community</p>
              {isAuthenticated && (
                <Link to="/chat" className="btn-premium">Go to Chat</Link>
              )}
              {!isAuthenticated && (
                <Link to="/register" className="btn-premium">Register Free</Link>
              )}
            </div>
          <div className="premium-card">
            <div className="premium-icon">🎬</div>
            <h3>Movie Booking</h3>
            <p>Browse movies, select seats, and book tickets</p>
            {isAuthenticated && (
              <Link to="/movies" className="btn-premium">Open Movie Booking</Link>
            )}
            {!isAuthenticated && (
              <Link to="/login" className="btn-premium">Login to Continue</Link>
            )}
          </div>
            <div className="premium-card">
              <div className="premium-icon">📝</div>
              <h3>Blog & Articles</h3>
              <p>Create, publish, and manage your blog articles. Share your thoughts and expertise with the community</p>
              {isAuthenticated && (
                <Link to="/blog/my-articles" className="btn-premium">Go to Blog</Link>
              )}
              {!isAuthenticated && (
                <Link to="/register" className="btn-premium">Register Free</Link>
              )}
            </div>
            <div className="premium-card coming-soon">
              <div className="premium-icon">🛒</div>
              <h3>E-Commerce</h3>
              <p>Buy and sell products in our marketplace</p>
              <span className="badge">Coming Soon</span>
            </div>
          </div>
        </div>
      </section>

      <section className="features-section">
        <div className="container">
          <h2 className="section-title">Why Choose Us?</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🚀</div>
              <h3>Fast & Reliable</h3>
              <p>Lightning-fast tools with 99.9% uptime</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔒</div>
              <h3>Secure</h3>
              <p>Your data is safe and encrypted</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">💰</div>
              <h3>Free Tier</h3>
              <p>Start free with generous limits</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;

